import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mafc/trolly';

import { Entry, ServiceProductsType } from 'network/response/ICart';
import { getFormattedPrice } from 'utils/price.util';

import ExtendedWarrantyCaption from './ExtendedWarrantyCaption';

import { getProperNoun } from 'utils/helpers.util';
import COLORS from 'theme/colors';
import { ReactComponent as Shield } from 'assets/svgs/icon-shield.svg';
import { ReactComponent as Bulb } from 'assets/svgs/icon-bulb.svg';
import { ReactComponent as InfinityIcon } from 'assets/svgs/icon-infinity.svg';
import { useState } from 'react';

interface Props {
  entryNumber?: Entry['entryNumber'];
  serviceProducts: ServiceProductsType;
  toggleDrawer?: () => void;
  updateWarranty: (arg1: string) => void;
  selectedCode: string;
}
const ExtendedWarrantyDrawer: React.FC<Props> = ({
  entryNumber,
  serviceProducts,
  toggleDrawer,
  updateWarranty,
  selectedCode,
}) => {
  const [tabName, updateTabName] = useState('');

  const onTabsHandler = useCallback(
    (code: string) => () => {
      updateTabName(code);
    },
    []
  );
  const nameAttrVal = `warranty-${entryNumber}`;
  const { t } = useTranslation();
  const warantyPts = useMemo(
    () => [
      {
        icon: Bulb,
        text: t('wrntyPts.bulb'),
      },
      {
        icon: Shield,
        text: t('wrntyPts.shield'),
      },
      {
        icon: InfinityIcon,
        text: t('wrntyPts.infinity'),
      },
    ],
    [t]
  );

  const onConfirm = useCallback(() => {
    updateWarranty(tabName || selectedCode);
    if (toggleDrawer) {
      toggleDrawer();
    }
  }, [selectedCode, tabName, toggleDrawer, updateWarranty]);
  return (
    <>
      <Box fontWeight="400">
        {warantyPts.map(curr => {
          const ComponentIcon = curr.icon;
          return (
            <Box
              key={curr.text}
              lineHeight="20px"
              display="flex"
              marginBottom="12px"
              alignItems="center"
            >
              <Box
                as="span"
                sx={{
                  background: COLORS.WHITE_25,
                  borderRadius: '50%',
                  marginRight: '12px',
                  display: 'flex',
                  minWidth: '48px',
                  height: '48px',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ComponentIcon />
              </Box>
              <Box as="p" color={COLORS.BLACK}>
                {curr.text}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box
        as="p"
        sx={{
          fontWeight: '700',
          fontSize: '16px',
          color: COLORS.BLACK,
          textAlign: 'center',
          margin: '0 0 16px 0',
        }}
      >
        {t('availableOptions')}
      </Box>
      <Box display="flex" justifyContent="space-between" sx={{ gap: '0 16px' }} flexWrap="wrap">
        {serviceProducts.map(({ code, price, yearOfWarranty }) => {
          const uniqID = `${nameAttrVal}-${code}`;
          const isChecked = tabName !== '' ? code === tabName : code === selectedCode;

          return (
            <Box
              key={uniqID}
              id={uniqID}
              name={nameAttrVal}
              value={code}
              checked={isChecked}
              className="extended-warranty"
              sx={{
                border: `1px solid ${isChecked ? COLORS.BLUE : COLORS.POLO_BLUE_25}`,
                width: `calc(${100 / serviceProducts.length}% - 8px )`,
                background: `${isChecked ? COLORS.POLO_BLUE_25 : 'auto'}`,
                cursor: `${isChecked ? 'auto' : 'pointer'}`,
                fontFamily: 'Montserrat',
                padding: '8px 32px',
                fontSize: '14px',
                borderRadius: '4px',
                color: `${!isChecked ? COLORS.BLACK : COLORS.BLUE}`,
                textAlign: 'center',
              }}
              onClick={onTabsHandler(code)}
            >
              <ExtendedWarrantyCaption
                display={'block'}
                fontSize={2}
                fontWeight={5}
                lineHeight="20px"
              >
                {getProperNoun(
                  yearOfWarranty,
                  t('extendedWarrantyMessage', {
                    count: yearOfWarranty,
                  }),
                  t('extendedWarrantyMessagePlural', {
                    count: yearOfWarranty,
                  })
                )}
              </ExtendedWarrantyCaption>
              <ExtendedWarrantyCaption display={'block'} fontSize={2}>
                {getFormattedPrice(price, false, true, true)}
              </ExtendedWarrantyCaption>{' '}
            </Box>
          );
        })}
        <Button
          variant="primary"
          width="100%"
          my="4"
          onClick={onConfirm}
          data-testid="button-add-wrnty"
        >
          <Box
            as="span"
            sx={{
              textTransform: 'capitalize',
              fontWeight: '700',
            }}
          >
            {t('addWarrantyCTA')}
          </Box>
        </Button>
      </Box>
    </>
  );
};

export default ExtendedWarrantyDrawer;
